// Un despliegue pendiente de tu Allow, entero (sprint 127 F4): qué es, QUÉ VERSIÓN se ejecuta de verdad (SHA y
// su PR; en un despliegue de Calypso, la que resuelve el gate o la de la vuelta atrás, no la punta de master),
// los cambios de ajustes (.env), rama, quién lo lanzó, cuándo y cómo se deshace. Arriba, en rojo, lo que el
// servidor no ha podido leer o no cuadra: la ventana nunca rellena un hueco con una suposición. Abajo, tu
// contraseña del Allow y «Rechazar» / «Allow». La primera vez, en su lugar, se fija la contraseña.
// La contraseña vive solo en el estado de esta ventana mientras está abierta: se borra al cerrar o enviar.
import { ArrowLeft, Check, ChevronRight, ExternalLink, KeyRound, Loader2, Rocket } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useToastStore } from '../../../stores/toast.store';
import { mensajeError } from '../errores';
import { formatClockTime, formatFechaHora } from '../../../lib/time';
import { hace } from '../marco/estado';
import { Aviso, Boton, Campo, Etiqueta, Pastillas, Ventana } from '../ui';
import { desplieguesApi, type Despliegue, type ItemSube } from './api';
import { LineasSubida, lineasQueSalen } from './LineasSubida';
import { Devolver, Hablar, MasInformacion, QuienLoPidio } from './Profundizar';
import { useBandejaStore } from './store';

/** Qué se pulsa al decidir: «devolver» es rechazar CON la nota; «rechazar» (sin más) nunca la lleva. */
export type AccionDecidir = 'aprobar' | 'rechazar' | 'devolver';

/**
 * Lo que se manda al servidor por cada botón. 29-09: «Rechazar sin más» hacía setNota('') y enseguida
 * decidir('rechazar'), que leía la nota VIEJA (el estado aún no había cambiado): la nota se mandaba igual.
 * Ahora la nota solo sale del botón «devolver», sin depender de cuándo se actualiza el estado.
 */
export function peticionDecidir(accion: AccionDecidir, nota: string): { decision: 'aprobar' | 'rechazar'; nota?: string } {
  if (accion === 'aprobar') return { decision: 'aprobar' };
  if (accion === 'rechazar') return { decision: 'rechazar' };
  const limpia = nota.trim();
  return limpia ? { decision: 'rechazar', nota: limpia } : { decision: 'rechazar' };
}

export function VentanaDespliegue({ despliegue: d, alCerrar }: { despliegue: Despliegue | null; alCerrar: () => void }) {
  const bandeja = useBandejaStore((s) => s.bandeja);
  const simulada = useBandejaStore((s) => s.simulada);
  const cargar = useBandejaStore((s) => s.cargar);
  const [contrasena, setContrasena] = useState('');
  const [enviando, setEnviando] = useState<null | AccionDecidir>(null);
  const [error, setError] = useState<string | null>(null);
  const [fijando, setFijando] = useState(false);
  // Brief §10: profundizar (más información, hablar con quien lo pidió) y devolver con una nota.
  const [vista, setVista] = useState<'resumen' | 'info' | 'hablar'>('resumen');
  const [devolviendo, setDevolviendo] = useState(false);
  const [nota, setNota] = useState('');
  // 13-10, «Quitar»: las líneas que Pau saca de esta subida (las que arrastran se calculan aparte).
  const [quitadas, setQuitadas] = useState<ReadonlySet<string>>(new Set());
  // Crítica 3 (C2): si Pau ha preguntado algo y aún no le han contestado, aprobar pasa a ser «igualmente».
  const hilo = useBandejaStore((s) => (d ? s.hilos[d.runId] : undefined));
  const sinContestar = hilo?.at(-1)?.de === 'pau' ? hilo.at(-1)!.texto : null;
  const preparando = d?.esDeploy && d.lineas.estado === 'preparando';

  useEffect(() => {
    setContrasena('');
    setError(null);
    setFijando(false);
    setVista('resumen');
    setDevolviendo(false);
    setNota('');
    setQuitadas(new Set());
  }, [d?.runId]);

  // Las líneas tardan 1-3 min en prepararse: mientras la tarjeta está abierta se pregunta cada 5 s, no cada 30.
  useEffect(() => {
    if (!preparando) return;
    const id = setInterval(() => void cargar(), 5000);
    return () => clearInterval(id);
  }, [preparando, cargar]);

  const cerrar = () => {
    setContrasena('');
    alCerrar();
  };

  const listas = d?.esDeploy && d.lineas.estado === 'listo' ? d.lineas : null;
  const fuera = listas ? lineasQueSalen(listas.lineas, quitadas) : new Set<string>();
  const quedan = listas ? listas.lineas.length - fuera.size : 0;

  async function decidir(accion: AccionDecidir) {
    if (!d || !contrasena || enviando) return;
    const { decision, nota: notaEnviada } = peticionDecidir(accion, nota);
    const quitar = decision === 'aprobar' ? [...quitadas] : [];
    setEnviando(accion);
    setError(null);
    try {
      if (!simulada) await desplieguesApi.decidir(d, decision, contrasena, notaEnviada, quitar);
      setContrasena('');
      const quien = d.pedidoPor?.titulo;
      useToastStore.getState().push({
        message: quitar.length
          ? t('oficinaV2.despliegues.lineas.aprobadoQuitando', { n: fuera.size })
          : notaEnviada && quien
            ? t('oficinaV2.despliegues.devuelto', { quien })
            : t(decision === 'aprobar' ? 'oficinaV2.despliegues.aprobado' : 'oficinaV2.despliegues.rechazado', { workflow: d.paraPau.titulo }),
      });
      void cargar(true);
      alCerrar();
    } catch (e) {
      setError(mensajeError(e));
      setContrasena('');
      void cargar(true);
    } finally {
      setEnviando(null);
    }
  }

  if (!d) return null;
  const fijada = bandeja?.contrasenaFijada ?? false;
  const bloqueado = bandeja?.bloqueadoHasta ?? null;
  const textoAllow = fuera.size
    ? t('oficinaV2.despliegues.lineas.allowParcial', { n: quedan, m: fuera.size })
    : t(sinContestar ? 'oficinaV2.despliegues.allowIgualmente' : 'oficinaV2.despliegues.allow');

  const pie = !fijada ? (
    fijando ? null : (
      <Boton variante="primario" icono={KeyRound} onClick={() => setFijando(true)}>
        {t('oficinaV2.despliegues.fijarBoton')}
      </Boton>
    )
  ) : (
    <form
      className="grid w-full grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap"
      // Crítica 3 (C3): Intro NUNCA aprueba; se aprueba solo pulsando el botón.
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="col-span-2 min-w-[220px] flex-1">
        <Etiqueta titulo={bandeja?.conCodigo ? t('oficinaV2.despliegues.tuCodigo') : t('oficinaV2.despliegues.tuContrasena')}>
          <Campo
            type={bandeja?.conCodigo ? 'text' : 'password'}
            inputMode={bandeja?.conCodigo ? 'numeric' : undefined}
            // Que el navegador no la guarde ni la rellene sola: se escribe a mano en cada Allow.
            autoComplete="one-time-code"
            value={contrasena}
            maxLength={200}
            disabled={!!bloqueado}
            onChange={(e) => setContrasena(e.target.value)}
            className="h-11"
          />
        </Etiqueta>
      </div>
      {devolviendo ? (
        <>
          <Boton disabled={!contrasena || !!bloqueado} cargando={enviando === 'rechazar'} onClick={() => void decidir('rechazar')} className="h-11">
            {t('oficinaV2.despliegues.rechazarSinMas')}
          </Boton>
          <Boton variante="primario" disabled={!contrasena || !nota.trim() || !!bloqueado} cargando={enviando === 'devolver'} onClick={() => void decidir('devolver')} className="h-11">
            {t('oficinaV2.despliegues.devolverBoton')}
          </Boton>
        </>
      ) : (
        <>
          <Boton disabled={!!bloqueado} onClick={() => { setDevolviendo(true); setVista('resumen'); }} className="h-11">
            {t('oficinaV2.despliegues.devolver')}
          </Boton>
          {/* Quitarlo todo no es un Allow: para eso, «Algo no me convence». */}
          <Boton variante="primario" icono={Check} disabled={!contrasena || !!bloqueado || (!!listas && listas.lineas.length > 0 && quedan === 0)} cargando={enviando === 'aprobar'} onClick={() => void decidir('aprobar')} className="h-11">
            {textoAllow}
          </Boton>
        </>
      )}
    </form>
  );

  // Quién lo pide y cuánto lleva esperando, en una línea bajo el título.
  const quien = d.pedidoPor
    ? `${t(d.pedidoPor.seguro ? 'oficinaV2.despliegues.lineas.loPide' : 'oficinaV2.despliegues.lineas.creoQueLoPide', { quien: d.pedidoPor.nombre ?? d.pedidoPor.titulo })} · `
    : '';
  const descripcion = `${quien}${t('oficinaV2.despliegues.esperaDesde', { hace: hace(d.creadoEn) })}`;

  return (
    <Ventana abierta={!!d} alCerrar={cerrar} ancho="lg" completaEnMovil titulo={<TituloSubida d={d} conIcono={!listas} />} descripcion={descripcion} pie={pie}>
      <div className="space-y-6 pb-2">
        {listas ? (
          // ── La tarjeta nueva (13-10): una línea por cosa que cambia ──
          <>
            {devolviendo ? <Devolver d={d} nota={nota} alCambiarNota={setNota} alCancelar={() => setDevolviendo(false)} /> : null}
            {!devolviendo && vista !== 'resumen' ? (
              <div className="space-y-4">
                <Boton variante="fantasma" tamano="sm" icono={ArrowLeft} onClick={() => setVista('resumen')} className="-ml-3">
                  {t('oficinaV2.despliegues.lineas.volver')}
                </Boton>
                {vista === 'info' ? <MasInformacion d={d} /> : <Hablar d={d} />}
              </div>
            ) : (
              <>
                <Notas d={d} />
                {d.avisos.map((a) => (
                  <Aviso key={a} tono="error">
                    {a}
                  </Aviso>
                ))}
                <LineasSubida
                  d={d}
                  lineas={listas.lineas}
                  sinEfecto={listas.sinEfecto}
                  quitadas={quitadas}
                  alQuitar={(id) => setQuitadas((q) => new Set([...q, id]))}
                  alDevolver={(id) => setQuitadas((q) => new Set([...q].filter((x) => x !== id)))}
                />
                {fuera.size ? <p className="px-1 text-o-texto leading-snug text-oficina-texto2">{t('oficinaV2.despliegues.lineas.trasQuitar')}</p> : null}
                <MasDetalles d={d} alVer={setVista} />
              </>
            )}
          </>
        ) : (
          // ── Sin líneas (otro trabajo, vuelta atrás, preparándose o no disponibles): la tarjeta de siempre ──
          <>
            <div className="space-y-4">
              <QuienLoPidio d={d} />
              {devolviendo ? (
                <Devolver d={d} nota={nota} alCambiarNota={setNota} alCancelar={() => setDevolviendo(false)} />
              ) : (
                <Pastillas
                  llenar
                  etiqueta={t('oficinaV2.despliegues.vistas')}
                  valor={vista}
                  alCambiar={setVista}
                  opciones={[
                    { valor: 'resumen', texto: t('oficinaV2.despliegues.vistaResumen') },
                    { valor: 'info', texto: t('oficinaV2.despliegues.vistaInfo') },
                    { valor: 'hablar', texto: t('oficinaV2.despliegues.vistaHablar') },
                  ]}
                />
              )}
            </div>
            {!devolviendo && vista === 'info' ? <MasInformacion d={d} /> : null}
            {!devolviendo && vista === 'hablar' ? <Hablar d={d} /> : null}
            {devolviendo || vista === 'resumen' ? (
              <>
                {d.avisos.map((a) => (
                  <Aviso key={a} tono="error">
                    {a}
                  </Aviso>
                ))}
                <EstadoLineas d={d} />
                {/* Lo que lee Pau (28-09: «todo más masticado; la versión y el código son ruido»): qué cambia, qué
                    aprueba y qué pasa si sale mal. Lo técnico, plegado abajo para quien lo necesite. */}
                {d.loQueSube.estado === 'ok' ? <QueSube items={d.loQueSube.items} total={d.loQueSube.total} /> : null}
                {d.paraPau.cambiaEstado !== 'no_aplica' ? (
                  <Seccion titulo={t('oficinaV2.despliegues.queCambia')}>
                    {d.paraPau.cambiaEstado === 'listo' ? (
                      <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.cambia}</p>
                    ) : d.paraPau.cambiaEstado === 'preparando' ? (
                      <p className="text-o-cuerpo text-oficina-texto3">{t('oficinaV2.despliegues.preparando')}</p>
                    ) : (
                      <p className="text-o-cuerpo text-oficina-texto2">{t('oficinaV2.despliegues.sinResumen')}</p>
                    )}
                  </Seccion>
                ) : null}
                <Seccion titulo={t('oficinaV2.despliegues.queApruebas')}>
                  <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.apruebas}</p>
                </Seccion>
                {d.paraPau.siRechazas ? (
                  <Seccion titulo={t('oficinaV2.despliegues.siRechazas')}>
                    <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.siRechazas}</p>
                  </Seccion>
                ) : null}
                <Seccion titulo={t('oficinaV2.despliegues.siSaleMal')}>
                  <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.siSaleMal}</p>
                </Seccion>
                <details className="group rounded-o-m bg-white/[0.03] ring-1 ring-white/[0.06]">
                  <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-o-texto font-medium text-oficina-texto2 outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-enlace/70 [&::-webkit-details-marker]:hidden">
                    <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />
                    {t('oficinaV2.despliegues.detallesTecnicos')}
                  </summary>
                  <div className="px-4 pb-4">
                    <DetallesTecnicos d={d} />
                  </div>
                </details>
              </>
            ) : null}
          </>
        )}

        {bloqueado ? <Aviso tono="error">{t('oficinaV2.despliegues.bloqueado', { hora: formatClockTime(bloqueado) })}</Aviso> : null}
        {!fijada && fijando ? <FijarContrasena alTerminar={() => setFijando(false)} /> : null}
        {!fijada && !fijando ? <Aviso>{t('oficinaV2.despliegues.sinContrasena')}</Aviso> : null}
        {sinContestar && !devolviendo ? <Aviso tono="aviso">{t('oficinaV2.despliegues.sinContestar', { pregunta: sinContestar })}</Aviso> : null}
        {error ? <Aviso tono="error">{error}</Aviso> : null}
      </div>
    </Ventana>
  );
}

/** El título: la frase del agente para Pau (lo que aprueba, en una frase). */
function TituloSubida({ d, conIcono }: { d: Despliegue; conIcono: boolean }) {
  if (!conIcono) return <span className="block text-o-titulo3 font-semibold leading-snug tracking-[-0.01em] sm:text-o-titulo2">{d.paraPau.titulo}</span>;
  return (
    <span className="flex items-center gap-2">
      <Rocket className="h-5 w-5 text-oficina-bloqueado" aria-hidden />
      {d.paraPau.titulo}
    </span>
  );
}

/** Lo que vuelve tras una nota tuya o algo que ya aprobaste: arriba, en una frase. */
function Notas({ d }: { d: Despliegue }) {
  const notas = [d.vuelveTrasNota ? t('oficinaV2.despliegues.vuelveTrasNota', { nota: d.vuelveTrasNota }) : null, d.antecedente].filter((x): x is string => !!x);
  if (!notas.length) return null;
  return (
    <div className="space-y-2">
      {notas.map((n) => (
        <p key={n} className="rounded-o-m bg-oficina-acento/10 px-4 py-3 text-o-texto leading-snug text-oficina-texto ring-1 ring-oficina-acento/25">
          {n}
        </p>
      ))}
    </div>
  );
}

/** Sin líneas en una subida de Calypso: o se están preparando (y se dice), o no se han podido hacer (y se dice). */
function EstadoLineas({ d }: { d: Despliegue }) {
  if (!d.esDeploy || d.vueltaAtras) return null;
  if (d.lineas.estado === 'preparando') {
    return (
      <p className="flex items-center gap-3 rounded-o-m bg-white/[0.035] px-4 py-3 text-o-cuerpo leading-snug text-oficina-texto2 ring-1 ring-white/[0.07]">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        {t('oficinaV2.despliegues.lineas.preparando')}
      </p>
    );
  }
  if (d.lineas.estado === 'no_disponible') return <Aviso tono="aviso">{t('oficinaV2.despliegues.lineas.noDisponible')}</Aviso>;
  return null;
}

/** Lo que no hace falta para decidir, plegado: si lo rechazas, si sale mal, la explicación larga y lo técnico. */
function MasDetalles({ d, alVer }: { d: Despliegue; alVer: (v: 'info' | 'hablar') => void }) {
  return (
    <details className="group overflow-hidden rounded-o-l bg-white/[0.035] ring-1 ring-white/[0.07]">
      <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 px-4 py-3 text-o-cuerpo text-oficina-texto2 outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 sm:px-5 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">{t('oficinaV2.despliegues.lineas.masDetalles')}</span>
        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden />
      </summary>
      <div className="space-y-5 px-4 pb-5 sm:px-5">
        {d.paraPau.siRechazas ? (
          <Seccion titulo={t('oficinaV2.despliegues.siRechazas')}>
            <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.siRechazas}</p>
          </Seccion>
        ) : null}
        <Seccion titulo={t('oficinaV2.despliegues.siSaleMal')}>
          <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.paraPau.siSaleMal}</p>
        </Seccion>
        <div className="-ml-3 flex flex-wrap gap-1">
          <Boton variante="fantasma" tamano="sm" onClick={() => alVer('info')}>
            {t('oficinaV2.despliegues.lineas.verExplicacion')}
          </Boton>
          {d.pedidoPor?.vivo ? (
            <Boton variante="fantasma" tamano="sm" onClick={() => alVer('hablar')}>
              {t('oficinaV2.despliegues.lineas.hablarSubida', { quien: d.pedidoPor.nombre ?? d.pedidoPor.titulo })}
            </Boton>
          ) : null}
        </div>
        <div className="border-t border-white/[0.07] pt-5">
          <h3 className="mb-3 text-o-sub font-semibold text-oficina-texto3">{t('oficinaV2.despliegues.detallesTecnicos')}</h3>
          <DetallesTecnicos d={d} />
        </div>
      </div>
    </details>
  );
}

function DetallesTecnicos({ d }: { d: Despliegue }) {
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-o-cuerpo">
        <Dato titulo={t('oficinaV2.despliegues.version')}>
          {d.sha ? (
            <span className="font-mono text-o-texto">{d.sha.slice(0, 8)}</span>
          ) : (
            <span className="font-semibold text-oficina-error">{t('oficinaV2.despliegues.versionDesconocida')}</span>
          )}
          {d.vueltaAtras ? <span className="ml-2 font-semibold text-oficina-bloqueado">{t('oficinaV2.despliegues.vueltaAtras', { sha: d.vueltaAtras })}</span> : null}
          {d.pr ? (
            <a href={d.pr.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-oficina-enlace hover:underline">
              {t('oficinaV2.despliegues.propuesta', { n: d.pr.numero })}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
        </Dato>
        {d.gitRef ? (
          <Dato titulo={t('oficinaV2.despliegues.pedidoDesplegar')}>
            <span className="break-all font-mono text-o-texto">{d.gitRef}</span>
          </Dato>
        ) : null}
        <Dato titulo={t('oficinaV2.despliegues.rama')}>
          <span className={d.rama === 'master' ? 'font-mono text-o-texto' : 'font-mono text-o-texto font-semibold text-oficina-error'}>{d.rama || t('oficinaV2.desconocido')}</span>
        </Dato>
        {d.esDeploy ? (
          <Dato titulo={t('oficinaV2.despliegues.copiasNginx')}>
            {d.copias === null || d.refrescaNginx === null
              ? <span className="text-oficina-texto2">{t('oficinaV2.despliegues.copiasNoSeVen')}</span>
              : t('oficinaV2.despliegues.copiasSi', { copias: d.copias, nginx: t(d.refrescaNginx ? 'oficinaV2.despliegues.nginxSi' : 'oficinaV2.despliegues.nginxNo') })}
          </Dato>
        ) : null}
        <Dato titulo={t('oficinaV2.despliegues.lanzadoPor')}>{d.lanzadoPor || t('oficinaV2.desconocido')}</Dato>
        <Dato titulo={t('oficinaV2.despliegues.cuando')}>
          {formatFechaHora(d.creadoEn)}
          <span className="text-oficina-texto3"> · {hace(d.creadoEn)}</span>
        </Dato>
      </dl>

      {d.esDeploy ? (
        <section>
          <h3 className="mb-2 text-o-sub font-semibold text-oficina-texto2">{t('oficinaV2.despliegues.cambiosEnv')}</h3>
          {d.cambiosEnv.tipo === 'valor' ? (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-o-s bg-oficina-fondo p-3 font-mono text-o-sub leading-relaxed text-oficina-texto ring-1 ring-white/[0.06]">{d.cambiosEnv.texto}</pre>
          ) : d.cambiosEnv.tipo === 'desconocido' ? (
            <p className="text-o-cuerpo font-semibold text-oficina-error">{t('oficinaV2.despliegues.cambiosEnvDesconocido')}</p>
          ) : (
            <p className="text-o-cuerpo text-oficina-texto2">{t('oficinaV2.despliegues.sinCambiosEnv')}</p>
          )}
        </section>
      ) : null}

      <section>
        <h3 className="mb-2 text-o-sub font-semibold text-oficina-texto2">{t('oficinaV2.despliegues.comoSeDeshace')}</h3>
        <p className="text-o-cuerpo leading-relaxed text-oficina-texto">{d.comoSeDeshace}</p>
      </section>

      <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-o-texto text-oficina-enlace hover:underline">
        {t('oficinaV2.despliegues.verEnGithub')}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </a>
    </div>
  );
}

/**
 * 05-10 (Pau: «solo unas frases que me refresquen qué estoy aceptando»): TODO lo que sube, no solo lo de quien lo
 * pide. Primero lo que toca a los fans, con quién lo hizo y lo que te escribió al integrarlo; lo demás (análisis,
 * documentos, la oficina) plegado, porque no cambia lo que contesta a los fans.
 */
function QueSube({ items, total }: { items: ItemSube[]; total: number }) {
  const fans = items.filter((i) => i.afectaFans);
  const resto = items.filter((i) => !i.afectaFans);
  return (
    <Seccion titulo={t(total === 1 ? 'oficinaV2.despliegues.queSubeUno' : 'oficinaV2.despliegues.queSubeVarios', { n: total })}>
      {fans.length ? (
        <ul className="space-y-3">
          {fans.map((i) => (
            <CambioSube key={`${i.pr ?? i.titulo}`} i={i} />
          ))}
        </ul>
      ) : (
        <p className="text-o-cuerpo text-oficina-texto2">{t('oficinaV2.despliegues.ningunoFans')}</p>
      )}
      {resto.length ? (
        <details className="group mt-3">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-o-texto text-oficina-texto2 outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-enlace/70 [&::-webkit-details-marker]:hidden">
            <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />
            {t(resto.length === 1 ? 'oficinaV2.despliegues.restoUno' : 'oficinaV2.despliegues.restoVarios', { n: resto.length })}
          </summary>
          <ul className="mt-2 space-y-3">
            {resto.map((i) => (
              <CambioSube key={`${i.pr ?? i.titulo}`} i={i} />
            ))}
          </ul>
        </details>
      ) : null}
      {total > items.length ? <p className="mt-2 text-o-sub text-oficina-texto3">{t('oficinaV2.despliegues.yMas', { n: total - items.length })}</p> : null}
    </Seccion>
  );
}

function CambioSube({ i }: { i: ItemSube }) {
  return (
    <li className={cn('rounded-o-m px-3 py-2.5 ring-1', i.afectaFans ? 'bg-oficina-bloqueado/[0.08] ring-oficina-bloqueado/25' : 'bg-white/[0.03] ring-white/[0.06]')}>
      <p className="text-o-cuerpo font-semibold text-oficina-texto">
        {i.agente ? <span className="text-oficina-enlace">{i.agente} · </span> : null}
        {i.titulo}
        {i.url ? (
          <a href={i.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex align-middle text-oficina-texto3 hover:text-oficina-enlace" aria-label={t('oficinaV2.despliegues.verCambio')}>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        ) : null}
      </p>
      {i.readmite ? <p className="mt-0.5 text-o-sub font-semibold text-oficina-bloqueado">{t('oficinaV2.despliegues.vuelveRechazado')}</p> : null}
      {i.esRetirada ? <p className="mt-0.5 text-o-sub font-semibold text-oficina-texto3">{t('oficinaV2.despliegues.esRetirada')}</p> : null}
      <p className="mt-0.5 text-o-cuerpo leading-relaxed text-oficina-texto2">{i.resumen ?? t('oficinaV2.despliegues.sinResumenCambio')}</p>
    </li>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-texto2">{titulo}</h3>
      {children}
    </section>
  );
}

function Dato({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-oficina-texto3">{titulo}</dt>
      <dd className="min-w-0 text-oficina-texto">{children}</dd>
    </>
  );
}

/** La primera vez: fijar la contraseña personal del Allow, con la del panel. */
/** `suelta`: en su propia ventana (ya lleva título): sin recuadro ni título repetido. */
export function FijarContrasena({ alTerminar, suelta = false }: { alTerminar: () => void; suelta?: boolean }) {
  const cargar = useBandejaStore((s) => s.cargar);
  const simulada = useBandejaStore((s) => s.simulada);
  const [panel, setPanel] = useState('');
  const [nueva, setNueva] = useState('');
  const [repite, setRepite] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valida = nueva.length >= 10 && nueva === repite && panel.length > 0;

  async function fijar() {
    if (!valida) return;
    setEnviando(true);
    setError(null);
    try {
      if (!simulada) await desplieguesApi.fijarContrasena(panel, nueva);
      useToastStore.getState().push({ message: t('oficinaV2.despliegues.fijada') });
      await cargar(true);
      alTerminar();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setPanel('');
      setNueva('');
      setRepite('');
      setEnviando(false);
    }
  }

  return (
    <form
      className={suelta ? 'space-y-4' : 'space-y-4 rounded-o-m bg-white/[0.04] p-4 ring-1 ring-white/[0.08]'}
      onSubmit={(e) => {
        e.preventDefault();
        void fijar();
      }}
    >
      {suelta ? null : <p className="text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.despliegues.fijarTitulo')}</p>}
      <p className={cn('text-o-texto text-oficina-texto2', !suelta && '-mt-2')}>{t('oficinaV2.despliegues.fijarExplica')}</p>
      <Etiqueta titulo={t('oficinaV2.despliegues.contrasenaPanel')}>
        <Campo type="password" autoComplete="off" value={panel} maxLength={200} onChange={(e) => setPanel(e.target.value)} />
      </Etiqueta>
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <Etiqueta titulo={t('oficinaV2.despliegues.nueva')} ayuda={t('oficinaV2.despliegues.nuevaAyuda')}>
          <Campo type="password" autoComplete="new-password" value={nueva} maxLength={200} onChange={(e) => setNueva(e.target.value)} />
        </Etiqueta>
        <Etiqueta titulo={t('oficinaV2.despliegues.repite')}>
          <Campo type="password" autoComplete="new-password" value={repite} maxLength={200} onChange={(e) => setRepite(e.target.value)} />
        </Etiqueta>
      </div>
      {repite && nueva !== repite ? <p className="text-o-sub text-oficina-error">{t('oficinaV2.despliegues.noCoinciden')}</p> : null}
      {error ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="flex justify-end gap-2">
        <Boton onClick={alTerminar}>{t('oficinaV2.accion.cancelar')}</Boton>
        <Boton type="submit" variante="primario" disabled={!valida} cargando={enviando}>
          {t('oficinaV2.despliegues.fijarGuardar')}
        </Boton>
      </div>
    </form>
  );
}
